/**
 * Snapshot of the active user's local data (visitor or signed in) and its
 * transactional, idempotent import on the receiving device.
 *
 * - The snapshot consists of MATERIALIZED ROWS from the user's domain tables
 *   (`USER_DATA_TABLES`). There is no need to transfer the `ps_crud` queue:
 *   when inserted through the PowerSync API, the receiver generates its OWN
 *   equivalent upload operations (complete rows), so the sender's pending
 *   changes travel already materialized in their rows.
 * - Import: `user_id` is re-stamped with the receiver's user (same pattern as
 *   `sweepVisitorData`), and only rows whose `id` does not exist are inserted →
 *   re-importing is a no-op (idempotency by id).
 * - Security: the snapshot arrives from outside → allowlisted tables ONLY and
 *   columns validated against an identifier pattern before interpolation.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

import { USER_DATA_TABLES } from "@comandero/powersync";
import { isRecord } from "./guards";

/** Minimum DB surface (PowerSync satisfies it; tests inject a fake). */
export interface SnapshotLockContext {
  getAll<T>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<unknown>;
}

export interface SnapshotDb extends SnapshotLockContext {
  writeTransaction<T>(fn: (tx: SnapshotLockContext) => Promise<T>): Promise<T>;
}

export const SNAPSHOT_VERSION = 1;

export interface TransferSnapshot {
  version: number;
  tables: Record<string, Record<string, unknown>[]>;
}

export interface ImportCounts {
  inserted: number;
  skipped: number;
}

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export async function buildSnapshot(
  db: SnapshotLockContext,
  userId: string,
  tables: readonly string[] = USER_DATA_TABLES,
): Promise<TransferSnapshot> {
  const snapshot: TransferSnapshot = { version: SNAPSHOT_VERSION, tables: {} };
  for (const table of tables) {
    if (!IDENTIFIER_RE.test(table)) {
      throw new Error(`Invalid table name: ${table}`);
    }
    snapshot.tables[table] = await db.getAll<Record<string, unknown>>(
      `SELECT * FROM ${table} WHERE user_id = ?`,
      [userId],
    );
  }
  return snapshot;
}

export function snapshotRowCount(snapshot: TransferSnapshot): number {
  return Object.values(snapshot.tables).reduce((sum, rows) => sum + rows.length, 0);
}

export function parseSnapshotJson(json: string): TransferSnapshot | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== SNAPSHOT_VERSION || !isRecord(parsed.tables)) {
    return null;
  }
  // We rebuild the snapshot by validating every row (untrusted JSON):
  // no type assertions, only data that passes the guards enters.
  const tables: Record<string, Record<string, unknown>[]> = {};
  for (const [name, rows] of Object.entries(parsed.tables)) {
    if (!Array.isArray(rows)) {
      return null;
    }
    const validRows: Record<string, unknown>[] = [];
    const rowList: unknown[] = rows;
    for (const row of rowList) {
      if (!isRecord(row)) {
        return null;
      }
      validRows.push(row);
    }
    tables[name] = validRows;
  }
  return { version: SNAPSHOT_VERSION, tables };
}

/**
 * Imports the snapshot in a single write transaction.
 *
 * Idempotent: rows whose `id` already exists locally are skipped (local data
 * is never overwritten). Imported rows are re-stamped with `receiverUserId`,
 * so (a) the local UI shows them to the active user and (b) if the
 * receiver is signed in, the PowerSync queue uploads them with the correct owner.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function importSnapshot(
  db: SnapshotDb,
  snapshot: TransferSnapshot,
  receiverUserId: string,
  allowedTables: readonly string[] = USER_DATA_TABLES,
): Promise<ImportCounts> {
  if (!receiverUserId) {
    throw new Error("receiverUserId is required");
  }
  const counts: ImportCounts = { inserted: 0, skipped: 0 };

  await db.writeTransaction(async (tx) => {
    // Allowlist order (parents before children), not snapshot order.
    for (const table of allowedTables) {
      const rows = snapshot.tables[table];
      if (!rows?.length) {
        continue;
      }

      // Sender/receiver version skew: insert only columns that
      // exist locally (PRAGMA also works on PowerSync views).
      // No rows or no pragma → no extra filtering.
      const localColumns = new Set(
        (await tx.getAll<{ name: string }>(`PRAGMA table_info(${table})`)).map((c) => c.name),
      );

      const incomingIds = rows
        .map((row) => row.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      const existing = new Set<string>();
      const BATCH = 200;
      for (let i = 0; i < incomingIds.length; i += BATCH) {
        const batch = incomingIds.slice(i, i + BATCH);
        const placeholders = batch.map(() => "?").join(",");
        const found = await tx.getAll<{ id: string }>(
          `SELECT id FROM ${table} WHERE id IN (${placeholders})`,
          batch,
        );
        for (const row of found) {
          existing.add(row.id);
        }
      }

      for (const row of rows) {
        const { id } = row;
        if (typeof id !== "string" || id.length === 0) {
          counts.skipped += 1;
          continue;
        }
        if (existing.has(id)) {
          counts.skipped += 1;
          continue;
        }
        const stamped: Record<string, unknown> = { ...row };
        if ("user_id" in stamped) {
          stamped.user_id = receiverUserId;
        }
        const columns = Object.keys(stamped).filter(
          (c) => IDENTIFIER_RE.test(c) && (localColumns.size === 0 || localColumns.has(c)),
        );
        if (columns.length === 0) {
          counts.skipped += 1;
          continue;
        }
        const placeholders = columns.map(() => "?").join(", ");
        await tx.execute(
          `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`,
          columns.map((c) => stamped[c] ?? null),
        );
        counts.inserted += 1;
      }
    }
  });

  return counts;
}
